import React, { ReactNode, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface LayoutProps {
  children: ReactNode;
  currentPage?: string;
}

const Layout: React.FC<LayoutProps> = ({ children, currentPage }) => {
  const { user, logout, hasAnyPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  const activePage = currentPage || location.pathname.slice(1) || 'dashboard';

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleLogout = async () => {
    if (window.confirm('Bạn có chắc chắn muốn đăng xuất?')) {
      await logout();
      navigate('/login');
    }
  };

  const allMenuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊', module: 'dashboard' },
    { id: 'orders', label: 'Đơn hàng', icon: '📦', module: 'orders' },
    { id: 'users', label: 'Người dùng', icon: '👥', module: 'users' },
    { id: 'reports', label: 'Báo cáo', icon: '📈', module: 'reports' },
    { id: 'api-keys', label: 'Order API Keys', icon: '🔑', module: 'order_api_keys' },
    { id: 'social-count', label: 'Check Count API', icon: '\uD83D\uDD0E', module: 'social_count' },
    { id: 'instagram-cookies', label: 'Cookie Instagram', icon: '\uD83C\uDF6A', module: 'social_count' },
    { id: 'tiktok-cookies', label: 'Cookie TikTok', icon: '\uD83C\uDF6A', module: 'social_count' },
    { id: 'whatsapp', label: 'WhatsApp API', icon: '💬', module: 'whatsapp' },
    { id: 'jobs', label: 'Job Queue', icon: '📋', module: 'whatsapp' },
    { id: 'statistics', label: 'Statistics', icon: '📊', module: 'whatsapp' },
    { id: 'app-versions', label: 'App Versions', icon: '🔄', module: 'whatsapp' },
    { id: 'proxy-management', label: 'Proxy Management', icon: '🌐', module: 'settings' },
    { id: 'service-actions', label: 'Service Actions', icon: '⚙️', module: 'settings' },
    { id: 'support-requests', label: 'Support Requests', icon: '📋', module: 'settings' },
    { id: 'traodoisub', label: 'TraoDoiSub', icon: '🔄', module: 'settings' },
    { id: 'tuongtaccheo', label: 'TuongTacCheo', icon: '🔗', module: 'settings' },
  ];

  // Filter menu items based on permissions
  const menuItems = allMenuItems.filter(item => 
    hasAnyPermission(item.module)
  );

  const handleMenuClick = (itemId: string) => {
    navigate(`/${itemId}`);
    if (isMobile) {
      setIsMobileMenuOpen(false);
    }
  };

  const menuItemStyle = (isActive: boolean) => ({
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    color: isActive ? '#2563eb' : '#6b7280',
    backgroundColor: isActive ? '#eff6ff' : 'transparent',
    borderRadius: '8px',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: isActive ? '600' : '500',
    margin: '2px 0',
    cursor: 'pointer',
    border: 'none',
    width: '100%',
    textAlign: 'left' as const,
    transition: 'all 0.2s'
  });

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Mobile Header */}
      {isMobile && (
        <header style={{
          backgroundColor: 'white',
          borderBottom: '1px solid #e5e7eb',
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          zIndex: 1000
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '20px',
                cursor: 'pointer',
                marginRight: '12px',
                padding: '4px'
              }}
            >
              ☰
            </button>
            <h1 style={{ 
              fontSize: '16px', 
              fontWeight: '700', 
              color: '#1f2937',
              margin: 0
            }}>
              SMM Management
            </h1>
          </div>
          
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            backgroundColor: '#2563eb',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: '600'
          }}>
            {user?.fullName?.charAt(0) || 'U'}
          </div>
        </header>
      )}

      <div style={{ display: 'flex', minHeight: isMobile ? 'calc(100vh - 60px)' : '100vh' }}>
        {/* Sidebar */}
        <div style={{
          width: isMobile ? (isMobileMenuOpen ? '280px' : '0') : '250px',
          backgroundColor: 'white',
          boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
          padding: isMobile ? (isMobileMenuOpen ? '20px' : '0') : '20px',
          display: 'flex',
          flexDirection: 'column',
          position: isMobile ? 'fixed' : 'static',
          height: isMobile ? 'calc(100vh - 60px)' : 'auto',
          zIndex: 999,
          transition: 'all 0.3s ease',
          overflow: isMobile ? 'auto' : 'hidden',
          top: isMobile ? '60px' : '0'
        }}>
          {/* Desktop Logo */}
          {!isMobile && (
            <div style={{ marginBottom: '30px', textAlign: 'center' }}>
              <h1 style={{ 
                color: '#1f2937', 
                fontSize: '20px', 
                fontWeight: '700',
                margin: '0 0 5px 0' 
              }}>
                SMM Management
              </h1>
              <p style={{ 
                color: '#6b7280', 
                fontSize: '12px', 
                margin: 0 
              }}>
                Hệ thống quản lý đơn hàng
              </p>
            </div>
          )}

          {/* Menu */}
          <nav style={{ flex: 1 }}>
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleMenuClick(item.id)}
                style={menuItemStyle(activePage === item.id)}
                onMouseOver={(e) => {
                  if (activePage !== item.id) {
                    (e.target as HTMLElement).style.backgroundColor = '#f3f4f6';
                  }
                }}
                onMouseOut={(e) => {
                  if (activePage !== item.id) {
                    (e.target as HTMLElement).style.backgroundColor = 'transparent';
                  }
                }}
              >
                <span style={{ marginRight: '12px', fontSize: '16px' }}>
                  {item.icon}
                </span>
                {item.label}
              </button>
            ))}
          </nav>

          {/* User Info & Logout */}
          <div style={{
            borderTop: '1px solid #e5e7eb',
            paddingTop: '20px',
            marginTop: '20px'
          }}>
            {!isMobile && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '12px',
                padding: '8px'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: '600',
                  marginRight: '12px'
                }}>
                  {user?.fullName?.charAt(0) || 'U'}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#1f2937',
                    marginBottom: '2px'
                  }}>
                    {user?.fullName}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: '#6b7280'
                  }}>
                    {user?.role}
                  </div>
                </div>
              </div>
            )}
            
            <button
              onClick={handleLogout}
              style={{
                width: '100%',
                padding: '8px 12px',
                backgroundColor: '#f3f4f6',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '13px',
                color: '#374151',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                (e.target as HTMLElement).style.backgroundColor = '#e5e7eb';
              }}
              onMouseOut={(e) => {
                (e.target as HTMLElement).style.backgroundColor = '#f3f4f6';
              }}
            >
              Đăng xuất
            </button>
          </div>
        </div>

        {/* Mobile Overlay */}
        {isMobile && isMobileMenuOpen && (
          <div
            style={{
              position: 'fixed',
              top: '60px',
              left: '0',
              right: '0',
              bottom: '0',
              backgroundColor: 'rgba(0,0,0,0.5)',
              zIndex: 998
            }}
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}

        {/* Main Content */}
        <div style={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          minWidth: 0 // Important for flex container with potential overflow
        }}>
          {/* Desktop Header */}
          {!isMobile && (
            <header style={{
              backgroundColor: 'white',
              borderBottom: '1px solid #e5e7eb',
              padding: '16px 24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div>
                <h2 style={{
                  fontSize: '24px',
                  fontWeight: '700',
                  color: '#1f2937',
                  margin: 0,
                  textTransform: 'capitalize'
                }}>
                  {menuItems.find(item => item.id === activePage)?.label || 'Dashboard'}
                </h2>
              </div>
              
              <div style={{
                display: 'flex',
                alignItems: 'center',
                fontSize: '14px',
                color: '#6b7280'
              }}>
                <span style={{ marginRight: '8px' }}>🕐</span>
                {new Date().toLocaleString('vi-VN')}
              </div>
            </header>
          )}

          {/* Page Content */}
          <main style={{
            flex: 1,
            padding: isMobile ? '16px' : '24px',
            overflow: 'auto'
          }}>
            {children}
          </main>
        </div>
      </div>
    </div>
  );
};

export default Layout;